import { FC } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import type { TFunction } from 'i18next';
import Logger from 'utils/logger';
import PostTimeSaversApi, {
  PostWorkflowBatchResult,
  SendForApprovalParams,
} from 'app/api/postTimeSavers';
import { useToast } from 'app/hooks/useToast';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { UserActions } from 'modules/user/userActions';
import { FirstTimeFlagKey } from 'types/User';
import { useUser } from 'modules/user/userSelector';
import { getUserFirstTimeFlagStatus } from 'utils/user/firstTimeFlags';
import { useFirstApprovalRequestedModal } from 'app/components/modals/firstTimeFlags/firstApprovalRequested/FirstApprovalRequestedModal';
import {
  hasPostSentForApproval,
  markPostSentForApproval,
} from 'app/modules/onboardingChecklist/utils/sentForApprovalStorage';

type Reason = { key: string; defaultValue: string };

const GENERIC_REASON: Reason = {
  key: 'sendForApprovalResult.failureReason.generic',
  defaultValue: "Some posts couldn't be sent for approval.",
};

const REASON_BY_ERROR_CODE: Record<string, Reason> = {
  invalid_source_status: {
    key: 'sendForApprovalResult.failureReason.invalidStatus',
    defaultValue:
      "Some posts couldn't be sent because they can't be sent for approval from their current status.",
  },
  access_denied: {
    key: 'sendForApprovalResult.failureReason.accessDenied',
    defaultValue:
      "Some posts couldn't be sent because you don't have access to them.",
  },
  post_not_found: {
    key: 'sendForApprovalResult.failureReason.notFound',
    defaultValue: "Some posts couldn't be sent because they no longer exist.",
  },
  missing_permission: {
    key: 'sendForApprovalResult.failureReason.missingPermission',
    defaultValue:
      "Some posts couldn't be sent because you don't have permission to send them for approval.",
  },
};

function isBatchResult(value: unknown): value is PostWorkflowBatchResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as PostWorkflowBatchResult).failed) &&
    Array.isArray((value as PostWorkflowBatchResult).succeeded)
  );
}

export function buildSendForApprovalFailureMessage(
  result: unknown,
  t: TFunction,
): string | null {
  if (!isBatchResult(result) || result.failed.length === 0) {
    return null;
  }

  const total = result.succeeded.length + result.failed.length;

  const summary = t('sendForApprovalResult.summary', {
    defaultValue: '{{succeeded}} of {{total}} posts sent for approval.',
    succeeded: result.succeeded.length,
    total,
  });

  const seenKeys = new Set<string>();
  const reasons = result.failed
    .map(
      (failure) =>
        REASON_BY_ERROR_CODE[failure.error_code ?? ''] ?? GENERIC_REASON,
    )
    .filter((reason) => {
      if (seenKeys.has(reason.key)) {
        return false;
      }
      seenKeys.add(reason.key);
      return true;
    })
    .map((reason) => t(reason.key, { defaultValue: reason.defaultValue }))
    .join(' ');

  return `${summary} ${reasons}`.trim();
}

type Params = {
  submitSendForApproval: (params: Omit<SendForApprovalParams, 'type'>) => void;
  isLoading: boolean;
};

type Props = {
  children: (params: Params) => JSX.Element;
  successCallback?: (result: { hasPartialFailure: boolean }) => void;
  type: ApprovalType;
};

const RequestContainer: FC<Props> = ({ children, successCallback, type }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const user = useUser();
  const openFirstApprovalRequestedModal = useFirstApprovalRequestedModal(
    (state) => state.open,
  );
  const { mutate: mutateSendForApproval, isLoading: isSendForApprovalLoading } =
    useMutation(PostTimeSaversApi.sendForApproval, {
      onSuccess: (data) => {
        const partialFailureMessage = buildSendForApprovalFailureMessage(
          data?.batchResult,
          t,
        );

        if (partialFailureMessage) {
          toast(partialFailureMessage, 'warning');
        }

        const isFirstClientApprovalRequest =
          type === 'client' &&
          !hasPostSentForApproval(user.id, 'client') &&
          getUserFirstTimeFlagStatus(
            user.firstTimeFlags,
            FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_REQUESTED,
          ) === false;

        queryClient.invalidateQueries(queryKey.dashboardPosts());
        queryClient.invalidateQueries(queryKey.postRevisions());
        queryClient.invalidateQueries(queryKey.postsMeta());
        dispatch(
          UserActions.setFirstTimeFlag(
            type === 'client'
              ? FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_REQUESTED
              : FirstTimeFlagKey.FIRST_INTERNAL_APPROVAL_REQUESTED,
          ),
        );
        markPostSentForApproval(user.id, type);
        successCallback?.({
          hasPartialFailure: partialFailureMessage !== null,
        });

        if (isFirstClientApprovalRequest) {
          openFirstApprovalRequestedModal();
        }
      },
      onError: (error: ApiClientError) => {
        toast(error?.userMessage ?? t('somethingWentWrongTryAgain'), 'error');
        Logger.error(error);
      },
    });

  const handleSubmitSendForApproval = (
    formValues: Omit<SendForApprovalParams, 'type'>,
  ) => {
    mutateSendForApproval({ type, ...formValues });
  };

  return children({
    submitSendForApproval: handleSubmitSendForApproval,
    isLoading: isSendForApprovalLoading,
  });
};

export default RequestContainer;
